/* Product page extras: dispatch countdown, bulk tier picker, sticky add-to-cart. */
(() => {
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const DAY_NAMES = {
    Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday',
  };

  function londonNow() {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return {
      day: DAYS.indexOf(get('weekday')),
      seconds: Number(get('hour')) * 3600 + Number(get('minute')) * 60 + Number(get('second')),
    };
  }

  if (!customElements.get('pp-countdown')) {
    customElements.define(
      'pp-countdown',
      class extends HTMLElement {
        connectedCallback() {
          this.textEl = this.querySelector('[data-text]');
          this.cutoff = Number(this.dataset.cutoff || 15) * 3600;
          this.workDays = this.dataset.saturday === 'true' ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
          this.deliveryDays = Number(this.dataset.deliveryDays || 1);
          this.deliveryWeekdays = this.dataset.deliverSaturday === 'true' ? [1, 2, 3, 4, 5, 6] : [1, 2, 3, 4, 5];
          this.tick();
          this.timer = setInterval(() => this.tick(), 1000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
        formatTime(hourSeconds) {
          const h = Math.floor(hourSeconds / 3600);
          const suffix = h >= 12 ? 'pm' : 'am';
          const h12 = h % 12 === 0 ? 12 : h % 12;
          const m = Math.floor((hourSeconds % 3600) / 60);
          return m ? `${h12}:${String(m).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
        }
        deliveryLabel(dispatchOffset) {
          // dispatchOffset = days from today until dispatch; then count delivery working days
          const base = new Date();
          const londonToday = new Date(base.toLocaleString('en-US', { timeZone: 'Europe/London' }));
          const d = new Date(londonToday);
          d.setDate(d.getDate() + dispatchOffset);
          let remaining = this.deliveryDays;
          while (remaining > 0) {
            d.setDate(d.getDate() + 1);
            if (this.deliveryWeekdays.includes(d.getDay())) remaining--;
          }
          const diff = Math.round((d.setHours(0, 0, 0, 0) - londonToday.setHours(0, 0, 0, 0)) / 86400000);
          if (diff === 1) return 'tomorrow';
          return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        }
        tick() {
          const { day, seconds } = londonNow();
          const isWorkDay = this.workDays.includes(day);
          let dispatchOffset = 0;
          if (!(isWorkDay && seconds < this.cutoff)) {
            dispatchOffset = 1;
            while (!this.workDays.includes((day + dispatchOffset) % 7)) dispatchOffset++;
          }
          const dateLabel = `<strong>${this.deliveryLabel(dispatchOffset)}</strong>`;
          let html;
          if (isWorkDay && seconds < this.cutoff) {
            const left = this.cutoff - seconds;
            const h = Math.floor(left / 3600);
            const m = Math.floor((left % 3600) / 60);
            const s = left % 60;
            const time = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s).padStart(2, '0')}s`;
            html = this.escape(this.dataset.before)
              .replace('[countdown]', `<strong class="pp-countdown__time">${time}</strong>`)
              .replace('[date]', dateLabel);
          } else {
            let next = (day + 1) % 7;
            while (!this.workDays.includes(next)) next = (next + 1) % 7;
            const dayLabel = next === (day + 1) % 7 ? 'tomorrow' : DAY_NAMES[DAYS[next]];
            html = this.escape(this.dataset.after)
              .replace('[day]', `<strong>${dayLabel}</strong>`)
              .replace('[time]', this.formatTime(this.cutoff))
              .replace('[date]', dateLabel);
          }
          if (this.textEl.innerHTML !== html) this.textEl.innerHTML = html;
          this.classList.add('is-ready');
        }
        escape(str = '') {
          const d = document.createElement('div');
          d.textContent = str;
          return d.innerHTML;
        }
      }
    );
  }

  if (!customElements.get('pp-bulk-tiers')) {
    customElements.define(
      'pp-bulk-tiers',
      class extends HTMLElement {
        connectedCallback() {
          this.buttons = [...this.querySelectorAll('[data-qty]')];
          this.buttons.forEach((btn) => btn.addEventListener('click', () => this.select(btn)));
          this.input = document.getElementById(this.dataset.quantityInput);
          this.input?.addEventListener('change', () => this.sync());
          this.sync();
        }
        select(btn) {
          const input = document.getElementById(this.dataset.quantityInput);
          if (!input) return;
          input.value = btn.dataset.qty;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.sync();
        }
        sync() {
          const input = document.getElementById(this.dataset.quantityInput);
          const qty = Number(input?.value || 1);
          let active = null;
          this.buttons.forEach((b) => {
            if (qty >= Number(b.dataset.qty)) active = b;
          });
          this.buttons.forEach((b) => b.setAttribute('aria-pressed', b === active ? 'true' : 'false'));
        }
      }
    );
  }

  if (!customElements.get('pp-sticky-atc')) {
    customElements.define(
      'pp-sticky-atc',
      class extends HTMLElement {
        connectedCallback() {
          this.info = document.querySelector('product-info[id^="MainProduct-"]');
          if (!this.info) return;
          this.sectionId = this.info.dataset.section;
          this.button = this.querySelector('[data-sticky-button]');
          this.priceEl = this.querySelector('[data-sticky-price]');
          this.labelEl = this.querySelector('[data-sticky-label]');
          this.button.addEventListener('click', () => this.addToCart());

          this.observer = new IntersectionObserver(
            ([entry]) => {
              const above = entry.boundingClientRect.top < 0;
              this.toggle(!entry.isIntersecting && above);
            },
            { threshold: 0 }
          );
          this.watchTarget();
          this.sync();
          this.mutation = new MutationObserver(() => {
            this.sync();
            this.watchTarget();
          });
          this.mutation.observe(this.info, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
        }
        disconnectedCallback() {
          this.observer?.disconnect();
          this.mutation?.disconnect();
        }
        mainButton() {
          return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        }
        watchTarget() {
          const target = this.mainButton()?.closest('.product-form') || this.mainButton();
          if (!target || target === this.target) return;
          if (this.target) this.observer.unobserve(this.target);
          this.target = target;
          this.observer.observe(target);
        }
        toggle(show) {
          this.classList.toggle('is-visible', show);
          document.body.classList.toggle('pp-sticky-atc-open', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          this.button.tabIndex = show ? 0 : -1;
        }
        sync() {
          const subPrice = this.info.classList.contains('pp-sub-active') && this.info.querySelector('.pp-sub-price__now');
          const price = document.getElementById(`price-${this.sectionId}`);
          if (subPrice && this.priceEl) {
            this.priceEl.textContent = subPrice.textContent.trim();
          } else if (price && this.priceEl) {
            const sale = price.querySelector('.price--on-sale .price__sale .price-item--last');
            const regular = price.querySelector('.price__regular .price-item--regular');
            const text = (sale || regular)?.textContent.trim();
            if (text) this.priceEl.textContent = text;
          }
          const main = this.mainButton();
          if (main) {
            const disabled = main.hasAttribute('disabled');
            this.button.disabled = disabled;
            const label = main.querySelector('span:not(.hidden)')?.textContent.trim();
            if (label && this.labelEl) this.labelEl.textContent = label;
          }
        }
        addToCart() {
          const main = this.mainButton();
          if (!main || main.hasAttribute('disabled')) return;
          main.click();
        }
      }
    );
  }

  if (!customElements.get('pp-subscribe')) {
    customElements.define(
      'pp-subscribe',
      class extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.section;
          this.info = this.closest('product-info');
          this.input = this.querySelector('[data-plan-input]');
          this.choice = this.dataset.default === 'once' ? 'once' : 'sub';
          if (new URLSearchParams(location.search).get('selling_plan')) this.choice = 'sub';
          this.addEventListener('change', (e) => {
            if (e.target.matches('input[type="radio"]')) {
              this.choice = e.target.value;
              this.apply();
            } else if (e.target.matches('[data-plan-select]')) {
              this.apply();
            }
          });
          this.addEventListener('click', (e) => {
            const opt = e.target.closest('.pp-sub__opt--sub');
            if (opt && !e.target.closest('select, label') && this.choice !== 'sub') {
              this.choice = 'sub';
              this.apply();
            }
          });
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (data.sectionId !== this.sectionId) return;
              const fresh = data.html.getElementById(`PPSubscribe-${this.sectionId}`);
              const current = this.querySelector(`#PPSubscribe-${this.sectionId}`);
              const keepPlan = this.input.value;
              if (fresh && current) current.innerHTML = fresh.innerHTML;
              const sel = this.querySelector('[data-plan-select]');
              if (sel && keepPlan && sel.querySelector(`option[value="${keepPlan}"]`)) sel.value = keepPlan;
              // product-info resets the price + button after variant change; re-apply on the next frame
              requestAnimationFrame(() => this.apply());
            });
          }
          this.apply();
        }
        disconnectedCallback() {
          this.unsub?.();
        }
        currentPlan() {
          const sel = this.querySelector('[data-plan-select]');
          if (sel) return sel.options[sel.selectedIndex];
          return this.querySelector('[data-plan-single]');
        }
        apply() {
          const plan = this.currentPlan();
          const hasOnce = !!this.querySelector('.pp-sub__opt--once');
          if (!plan) {
            this.input.value = '';
            this.input.disabled = true;
            this.setActive(false);
            return;
          }
          if (!hasOnce) this.choice = 'sub';
          const isSub = this.choice === 'sub';

          this.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = r.value === this.choice));
          this.querySelectorAll('.pp-sub__opt').forEach((o) =>
            o.classList.toggle('is-selected', o.dataset.opt === this.choice)
          );
          this.input.value = isSub ? plan.value : '';
          this.input.disabled = !isSub;

          const percent = Number(plan.dataset.percent || 0);
          const badge = this.querySelector('[data-sub-badge]');
          const tpl = this.querySelector('[data-badge-template]')?.innerHTML || 'Save [percent]%';
          if (badge) {
            badge.hidden = percent <= 0;
            badge.textContent = tpl.replace('[percent]', percent);
          }
          const priceEl = this.querySelector('[data-sub-price]');
          const compareEl = this.querySelector('[data-sub-compare]');
          if (priceEl) priceEl.textContent = plan.dataset.price;
          if (compareEl) {
            compareEl.textContent = plan.dataset.compare;
            compareEl.hidden = percent <= 0;
          }

          this.setActive(isSub, plan, percent);
        }
        setActive(isSub, plan, percent) {
          if (!this.info) return;
          this.info.classList.toggle('pp-sub-active', !!isSub);

          // Top price swap
          const mainPrice = document.getElementById(`price-${this.sectionId}`);
          let top = this.info.querySelector('.pp-sub-price');
          if (this.dataset.topPrice === 'true' && isSub && mainPrice) {
            if (!top) {
              top = document.createElement('div');
              top.className = 'pp-sub-price';
              top.setAttribute('aria-live', 'polite');
              mainPrice.insertAdjacentElement('afterend', top);
            }
            const badgeText = (this.dataset.topBadge || '[percent]% OFF').replace('[percent]', percent);
            top.innerHTML = '';
            const now = document.createElement('span');
            now.className = 'pp-sub-price__now';
            now.textContent = plan.dataset.price;
            top.appendChild(now);
            if (percent > 0) {
              const was = document.createElement('s');
              was.className = 'pp-sub-price__was';
              was.textContent = plan.dataset.compare;
              const b = document.createElement('span');
              b.className = 'pp-sub-price__badge';
              b.textContent = badgeText;
              top.append(was, b);
            }
            top.hidden = false;
          } else if (top) {
            top.hidden = true;
          }

          // Button label
          const btn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const label = btn?.querySelector('span');
          if (btn && label) {
            if (!btn.dataset.ppOriginal) btn.dataset.ppOriginal = window.variantStrings?.addToCart || label.textContent.trim();
            if (!btn.hasAttribute('disabled')) {
              label.textContent = isSub && this.dataset.subButton ? this.dataset.subButton : btn.dataset.ppOriginal;
            }
          }
        }
      }
    );
  }
})();
(()=>{const t=customElements.get("pp-subscribe"),e=window.ShopifyAnalytics&&ShopifyAnalytics.meta&&ShopifyAnalytics.meta.product||{},n="PLA"===String(e.type||"").toUpperCase()||!e.type&&!!document.querySelector("pp-bulk-tiers"),a=()=>document.querySelectorAll("pp-bulk-tiers").forEach(t=>{n||(t.style.display="none")});if("loading"===document.readyState?document.addEventListener("DOMContentLoaded",a):a(),!t||!n)return;const s=t=>parseFloat(String(t||"").replace(/[^0-9.]/g,""))||0,r=t=>"£"+t.toFixed(2),o=t.prototype.apply;t.prototype.apply=function(){o.call(this);try{!function(t){const e=t.dataset.section,n=document.getElementById("Quantity-"+e),a=Math.max(1,parseInt(n&&n.value,10)||1),o=(t=>{let e=0;return document.querySelectorAll("pp-bulk-tiers [data-qty]").forEach(n=>{const a=n.textContent.match(/(\d+(?:\.\d+)?)\s*%/),s=a?parseFloat(a[1]):0;t>=Number(n.dataset.qty)&&s>e&&(e=s)}),e})(a)/100,p=t.closest("product-info"),c=t.querySelector(".pp-sub__opt--once .pp-sub__prices strong");let i=0;if(c){c.dataset.ppBase||(c.dataset.ppBase=s(c.textContent)),i=Number(c.dataset.ppBase);c.parentElement.innerHTML=(o?"<s>"+r(i)+"</s> ":"")+'<strong data-pp-base="'+i+'">'+r(i*(1-o))+"</strong>"}const d=t.currentPlan();if(!d)return;i||(i=s(d.dataset.compare));const l=s(d.dataset.price)*(1-o),u=i?Math.round(100*(1-l/i)):0,y=t.querySelector("[data-sub-price]"),m=t.querySelector("[data-sub-compare]"),b=t.querySelector("[data-sub-badge]");y&&(y.textContent=r(l));m&&(m.textContent=r(i),m.hidden=u<=0);b&&(b.hidden=u<=0,b.textContent="Save "+u+"%");const g="sub"===t.choice||!t.querySelector(".pp-sub__opt--once"),h=g?l:i*(1-o),f=i?Math.round(100*(1-h/i)):0,S=function(t,e){const n=t.closest("product-info");let a=n&&n.querySelector(".pp-sub-price");const s=document.getElementById("price-"+e);!a&&s&&(a=document.createElement("div"),a.className="pp-sub-price",a.setAttribute("aria-live","polite"),s.insertAdjacentElement("afterend",a));return a}(t,e);if(!S)return;f>0||a>1?(p.classList.toggle("pp-tier-active",!g),S.hidden=!1,S.innerHTML='<span class="pp-sub-price__now">'+r(h)+"</span>"+(f>0?'<s class="pp-sub-price__was">'+r(i)+'</s><span class="pp-sub-price__badge">'+f+"% OFF</span>":"")+'<span class="pp-tier-total">'+a+" spool"+(a>1?"s":"")+": "+r(h*a)+(g?" per delivery":"")+(a>=5?" · free delivery":"")+"</span>"):(p.classList.remove("pp-tier-active"),g||(S.hidden=!0))}(this)}catch(t){console.warn("pp tiers",t)}};const p=()=>document.querySelectorAll("pp-subscribe").forEach(t=>t.apply&&t.apply());document.addEventListener("change",t=>{t.target&&/^Quantity-/.test(t.target.id||"")&&p()},!0),document.addEventListener("click",t=>{t.target.closest&&t.target.closest(".quantity__button")&&setTimeout(p,50)}),document.addEventListener("input",t=>{t.target&&/^Quantity-/.test(t.target.id||"")&&p()}),p()})();
(()=>{const d=document.querySelector('.product__description');if(!d||d.scrollHeight<320)return;d.classList.add('pp-clamp');const b=document.createElement('button');b.type='button';b.className='pp-desc-toggle';b.textContent='Read more';b.setAttribute('aria-expanded','false');b.addEventListener('click',()=>{const o=d.classList.toggle('is-open');b.textContent=o?'Show less':'Read more';b.setAttribute('aria-expanded',o)});d.after(b)})();